import { useEffect, useState, useRef } from "react";
import {
  Box, Flex, Grid, IconButton, Image, Text, Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton, FormControl, FormLabel, Input, Button, FormErrorMessage,
  useDisclosure, Checkbox
} from "@chakra-ui/react";
import ContactDetailLogo from '../../../../../assets/images/ContactDetailLogo.svg';
import EditLogo from "../../../../../assets/images/EditLogo.svg";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useFetch } from "../../../../../hooks/useFetch";
import { CustomToast } from "../../../../../components";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

interface Props {
  employeeId: number | null;
}

export interface IContact {
  empId?: number | null;
  ivrUserName: string;
  ivrUserPassword: string;
  ivrUserPhone: string;

}

const ivrDisplayFields = [
  { label: "IVR Username", key: "ivrUserName" },
  { label: "IVR Password", key: "ivrUserPassword" },
  { label: "IVR UserPhoneNumber", key: "ivrUserPhone" },

];

const IvrCredential = ({ employeeId }: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);
  const [ivrData, setIvrData] = useState<IContact | null>(null);


  const IvrSchema = yup.object().shape({
    ivrUserName: yup
      .string()
      .required("IVR Username is required"),

    ivrUserPassword: yup
      .string()
      .required("IVR Password is required")
      .min(4, "Password must be at least 4 characters")
      .max(20, "Password cannot exceed 20 characters"),

    ivrUserPhone: yup
      .string()
      .required("IVR Username is required"),


  });


  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<IContact>({
    resolver: yupResolver(IvrSchema),
    defaultValues: {
      ivrUserName: "",
      ivrUserPassword: "",
      ivrUserPhone: "",

    },
  });

  async function loadIvrCredentials() {
    const response = await fetchApi(
      `Hrlense_Employee/ivrDetail?employeeId=${employeeId}`,
      "GET"
    );
    if (response) {
      setIvrData(response);
      reset({
        ivrUserName: response.ivrUserName || "",
        ivrUserPassword: response.ivrUserPassword || "",
        ivrUserPhone: response.ivrUserPhone || "",

      });
    } else {
      setIvrData(null);
      reset({
        ivrUserName: "",
        ivrUserPassword: "",
        ivrUserPhone: ""
      });
    }
  }

  useEffect(() => {
    if (employeeId) {
      loadIvrCredentials();
    }
  }, [employeeId]);

  const onSubmit = async (data: IContact) => {
    data.empId = employeeId;
    const response = await fetchApi(
      `Hrlense_Employee/ivrDetail/${employeeId}`,
      "PUT",
      data,
      null,
      "IVR Credentials Updated Successfully"
    );

    if (response) {
      onClose();
      loadIvrCredentials();
      addToast({ message: response.message, status: "success" });
    }
  };

  const handleClose = () => {
    onClose();
  };


  return (
    <>
      <Box pt={2} mb={6} className="main">
        <Flex justify="space-between" align="center" mb={2} pl={5} pr={5}>
          <Flex align="center" gap={3}>
            <Image src={ContactDetailLogo} alt={"Benifits & ID Numbers"} />
            <Text className="font-poppins text_sm text_medium col">
              Ivr Credential
            </Text>
          </Flex>
          <IconButton
            aria-label="Edit"
            icon={<Image src={EditLogo} />}
            variant="ghost"
            onClick={() => {
              onOpen();

            }}
          />
        </Flex>

        <Box py={2} px={10} bg="#FFFFFF" borderRadius={8}>
          {ivrData ? (
            ivrDisplayFields.map((data, index) => {
              let value = ivrData?.[data.key as keyof IContact] as string;
              return (
                <Grid
                  key={index}
                  templateColumns="1fr 1fr"
                  alignItems="center"
                >
                  <Text
                    className="font-poppins text_sm text_medium"
                    color="rgba(102, 102, 102, 1)"
                    mb={3}
                  >
                    {data.label}
                  </Text>

                  <Text className="font-poppins text_sm text_medium col">
                    {value || "-"}
                  </Text>
                </Grid>
              );
            })
          ) : (
            <Text className="font-poppins text_sm text_medium col">No IVR Credentials Found</Text>
          )}
        </Box>

      </Box>

      {/* Drawer for editing */}
      <Drawer isOpen={isOpen} placement="right" onClose={handleClose} size="md">

        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Update IVR Credentials</DrawerHeader>

          <DrawerBody>
            <form onSubmit={handleSubmit(onSubmit)}>
              <FormControl isInvalid={!!errors.ivrUserName} isRequired mb={4}>
                <FormLabel>IVR Username</FormLabel>
                <Controller
                  name="ivrUserName"
                  control={control}
                  render={({ field }) => (
                    <Input type="text" {...field} />
                  )}
                />
                <FormErrorMessage>
                  {errors.ivrUserName?.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.ivrUserPassword} isRequired mb={4}>
                <FormLabel>IVR Password</FormLabel>
                <Controller
                  name="ivrUserPassword"
                  control={control}
                  render={({ field }) => (
                    <Input  {...field} />
                  )}
                />
                <FormErrorMessage>
                  {errors.ivrUserPassword?.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.ivrUserPhone} isRequired mb={4}>
                <FormLabel>IVR Phone Number</FormLabel>
                <Controller
                  name="ivrUserPhone"
                  control={control}
                  render={({ field }) => (
                    <Input type="text" {...field} />
                  )}
                />
                <FormErrorMessage>
                  {errors.ivrUserPhone?.message}
                </FormErrorMessage>
              </FormControl>

              <Box mt={6} textAlign="right">
                <Button type="submit" className="btn_theme btn_theme:hover" isLoading={isSubmitting}>
                  Update
                </Button>
              </Box>
            </form>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default IvrCredential;
